import {
  BaseApiResponse,
  KycAccessResponse,
  PaginatedResponse,
} from "@interfaces/app.interface";
import { NotificationsInterfaceResponse } from "@interfaces/notifications.interface";
import {
  PropertyKind,
  PropertyListResponse,
  PropertyLocationResponse,
  PropertySpace,
  PropertySummaryResponse,
} from "@interfaces/properties.interface";
import {
  ChangePasswordRequest,
  ChangePinRequest,
  CreatePinRequest,
} from "@interfaces/user.interface";
import axios from "axios";
import customFetch from "@utils/customFetch";
import { toast } from "react-toastify";
import { WalletResponse } from "@interfaces/wallet.interface";
import { PriceSuggestionResponse } from "@pages/Listing/data";
import { fetchCookie } from "@utils/cookies";

const url = process.env["REACT_APP_API_BASE_URL"];

export interface TransactionEventPayload {
  status: string;
  bookingReference?: string | null;
  sessionExpiresAt?: string | null;
  hostAcceptedBankTransfer?: boolean;
  event?: string;
}

export const connectToTransactionEvents = (
  transactionId: string,
  onStatusChange: (payload: TransactionEventPayload) => void,
  onError?: (error: Event) => void,
): EventSource => {
  const token = fetchCookie("1Q_SPA");
  const params = new URLSearchParams();
  if (token) {
    params.set("token", token);
  }

  const sseUrl = `${url}/transactions/${transactionId}/events?${params.toString()}`;
  const eventSource = new EventSource(sseUrl);

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as TransactionEventPayload;
      if (data.status) {
        onStatusChange(data);
      }
    } catch (error) {
      console.error("Error parsing SSE message:", error);
    }
  };

  eventSource.onerror = (error) => {
    if (onError) {
      onError(error);
    }
  };

  return eventSource;
};

export const getBookingWaitStatus = (transactionId: string) => {
  return new Promise<{
    status: string;
    bookingReference?: string | null;
    sessionExpiresAt?: string | null;
    hostAcceptedBankTransfer?: boolean;
  }>((resolve, reject) => {
    return customFetch(
      `${url}/transactions/${transactionId}/booking-payment-status`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          const payload = data.data || {};
          resolve({
            status: payload.status,
            bookingReference: payload.bookingReference ?? null,
            sessionExpiresAt: payload.sessionExpiresAt ?? null,
            hostAcceptedBankTransfer: Boolean(payload.hostAcceptedBankTransfer),
          });
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const getProperties = (
  options: { [x: string]: string },
  signal: AbortSignal,
): Promise<
  BaseApiResponse<
    PropertyListResponse[],
    PaginatedResponse,
    PropertySummaryResponse
  >
> => {
  let query = Object.keys(options)
    .map((key) => key + "=" + options[key])
    .join("&");
  return new Promise((resolve, reject) => {
    return fetch(`${process.env["REACT_APP_API_BASE_URL"]}/listing?${query}`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then(
        (
          response: BaseApiResponse<
            PropertyListResponse[],
            PaginatedResponse,
            PropertySummaryResponse
          >,
        ) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      )
      .catch((err) => {
        reject(err);
      });
  });
};

export const getAvailableProperties = (
  options: { [x: string]: string },
  signal: AbortSignal,
): Promise<
  BaseApiResponse<
    PropertyListResponse[],
    PaginatedResponse,
    PropertySummaryResponse
  >
> => {
  const query = Object.keys(options)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(options[key])}`,
    )
    .join("&");

  return new Promise((resolve, reject) => {
    return fetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/available?${query}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then(
        (
          response: BaseApiResponse<
            PropertyListResponse[],
            PaginatedResponse,
            PropertySummaryResponse
          >,
        ) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      )
      .catch((err) => {
        reject(err);
      });
  });
};

export const getAllProperties = (
  signal: AbortSignal,
): Promise<
  BaseApiResponse<
    PropertyListResponse[],
    PaginatedResponse,
    PropertySummaryResponse
  >
> => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/listing`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then(
        (
          response: BaseApiResponse<
            PropertyListResponse[],
            PaginatedResponse,
            PropertySummaryResponse
          >,
        ) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      )
      .catch((err) => {
        reject(err);
      });
  });
};

export const getTopLocations = (
  signal: AbortSignal,
): Promise<BaseApiResponse<PropertyLocationResponse[]>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/top-locations`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<PropertyLocationResponse[]>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getUserNotifications = (
  user: string,
  signal: AbortSignal,
): Promise<BaseApiResponse<NotificationsInterfaceResponse[]>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/notifications/user/${user}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<NotificationsInterfaceResponse[]>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const getUnreadUserNotifications = (
  user: string,
  signal: AbortSignal,
): Promise<BaseApiResponse<NotificationsInterfaceResponse[]>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/notifications/user/${user}/unread-count`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<NotificationsInterfaceResponse[]>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const markReadNotification = (
  id: string,
  signal: AbortSignal,
): Promise<BaseApiResponse<NotificationsInterfaceResponse[]>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/notifications/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<NotificationsInterfaceResponse[]>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getPropertyKinds = (
  signal: AbortSignal,
): Promise<BaseApiResponse<PropertyKind[], PaginatedResponse>> => {
  return new Promise((resolve, reject) => {
    return fetch(`${process.env["REACT_APP_API_BASE_URL"]}/kinds`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<PropertyKind[], PaginatedResponse>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getPropertyAmenities = (
  signal: AbortSignal,
): Promise<BaseApiResponse<PropertyKind[], PaginatedResponse>> => {
  let _token: string | null = fetchCookie("1Q_SPA");
  return new Promise((resolve, reject) => {
    return fetch(`${process.env["REACT_APP_API_BASE_URL"]}/amenities`, {
      signal: signal,
      headers: {
        Authorization: `Bearer ${_token}`,
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<PropertyKind[], PaginatedResponse>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getPropertySpaces = (
  signal: AbortSignal,
): Promise<BaseApiResponse<PropertySpace[], PaginatedResponse>> => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/spaces`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<PropertySpace[], PaginatedResponse>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getProfile = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/auth/me/`, {
      signal,
      skipAuthRedirect: true,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        if (err.message && err.message.includes("401")) {
          reject({ status: 401, message: "Unauthorized" });
        } else {
          reject(err);
        }
      });
  });
};

export const updateProfile = (
  id: string,
  payload: any,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/users/${id}`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(payload),
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const updateProfilePic = (
  id: string,
  payload: any,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/${id}/profile-photo`,
      {
        signal: signal,
        method: "PUT",
        body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const uploadGovernmentId = (
  id: string,
  payload: any,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/government-id/${id}`,
      {
        signal: signal,
        method: "PUT",
        body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getWallet = (
  id: string,
  signal: AbortSignal,
): Promise<WalletResponse> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/wallets/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getWalletTransactions = (id: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/wallet-transactions/wallet/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getUnpaidCautionFees = (
  signal: AbortSignal,
  page: number = 1,
  perPage: number = 10,
  intent?: "shortlet" | "long-lease",
) => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (intent) params.append("intent", intent);

  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/caution-fees?${params.toString()}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getCautionFeeDetails = (
  transactionId: string,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${encodeURIComponent(transactionId)}/caution-fee`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok || body.success === false)
          throw new Error(
            body.message || "Unable to load caution fee details.",
          );
        return body;
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Checkout
export const checkout = (id: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/checkout`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json().then((data) => {
          if (!res.ok) {
            throw data;
          }
          return data;
        });
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// get bank and bank codes
export const getBankCodes = () => {
  // let _token: string | null = fetchCookie('1Q_SPA');
  return new Promise((resolve, reject) => {
    return customFetch("https://api.paystack.co/bank", {
      // signal: signal,
      headers: {
        //
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Link Account
export const LinkAccount = (payload: any, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/wallets/resolve-bank-account`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify(payload),
      },
    )
      .then((response: any) => {
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Save Account
export const saveAccount = ({ id, data }: any, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/wallets/${id}`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(data),
    })
      .then((response: any) => {
        return response.json();
      })
      .then((res) => {
        if (res.success) {
          resolve(res);
        } else {
          reject(res);
        }
      })
      .catch((err) => {
        reject(err);

        console.error("Error verifying account:", err);
      });
  });
};

// Change Payment Method Request
export const changePaymentMethodRequest = (
  walletId: string,
  data: any,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/wallets/${walletId}/payment-method/change-request`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      },
    )
      .then((response: any) => {
        return response.json();
      })
      .then((res) => {
        if (res.success) {
          resolve(res);
        } else {
          reject(res);
        }
      })
      .catch((err) => {
        reject(err);
        console.error("Error requesting payment method change:", err);
      });
  });
};

// Withdraw from wallet
export const withdrawFunds = ({ id, data }: any, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/wallets/${id}`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(data),
    })
      .then((response: any) => {
        return response.json();
      })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);

        console.error("Error withdrawing funds:", err);
      });
  });
};
// Request Deposit
export const requestDeposit = ({ id, data }: any, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/wallets/${id}/deposit`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify(data),
      },
    )
      .then((response: any) => {
        return response.json();
      })
      .then((res) => {
        if (res.success) {
          resolve(res);
        } else {
          reject(res);
        }
      })
      .catch((err) => {
        reject(err);

        console.error("Error withdrawing funds:", err);
      });
  });
};

export const getPropertyTransactions = (id: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/${id}/transactions`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const getAllPropertyTransactions = (
  id: string,
  signal: AbortSignal,
  page: number = 1,
  intent: string,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/${id}/transactions?paginate=true&page=${page}&intent=${intent}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getBookingsSummary = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/bookings/summary`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getSinglePropertyTransaction = (
  id: string,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// fetch all extended transactions
export const getExtendedTransactions = (
  id: string,
  userId: string,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/${userId}/transactions?originalTransaction=${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Accept Transactions
export const acceptTransaction = (id: string, couponCode?: string) => {
  const url = couponCode
    ? `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/accept?coupon=${couponCode}`
    : `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/accept`;

  return new Promise((resolve, reject) => {
    return customFetch(url, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const inspectionStatus = (id: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/inspection-status`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          status: "skipped",
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const updateInspectionStatusFalse = (id: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/inspection-done`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({ inspected: false }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const cancelTransaction = (id: string, reason: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/cancel`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({ reason }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const updateCancellation = (
  id: string,
  status: "rejected" | "completed",
) => {
  const body =
    status === "rejected" ? { status: "rejected" } : { status: "completed" };

  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/cancellations/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(body),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const reportIncident = (id: string, description: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/incidents`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({ description }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const updatePropertyAvailability = (
  id: string,
  payload: "available" | "unavailable",
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/update-availability`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          property_avalability_status: payload,
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const reconfirmPropertyAvailability = (
  id: string,
  payload: "available" | "unavailable",
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/reconfirm-availability`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          property_avalability_status: payload,
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const updateRescheduleAvailability = (
  id: string,
  payload: "available" | "unavailable",
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/reschedule/update-availability`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          property_avalability_status: payload,
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const pickInspectionForTransaction = (id: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/request-inspection`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const updateInspectionStatus = (id: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/inspection-done`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({ inspected: true }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const confirmInspectionForTransaction = (id: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/fix-inspection-date`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          inspection_date: payload,
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getSingleProperty = (slug: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/slug/${slug}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getSingleListingProperty = (id: string, signal: AbortSignal, term?: number, termUnit: "years" | "months" = "years") => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${encodeURIComponent(id)}${term == null ? "" : `?${termUnit}=${term}`}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getSimilarProperties = (
  id: string,
  radiusKm: number = 5,
  limit: number = 4,
  signal: AbortSignal,
): Promise<
  BaseApiResponse<
    PropertyListResponse[],
    PaginatedResponse,
    PropertySummaryResponse
  >
> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}/similar?radiusKm=${radiusKm}&limit=${limit}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then(
        (
          response: BaseApiResponse<
            PropertyListResponse[],
            PaginatedResponse,
            PropertySummaryResponse
          >,
        ) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      )
      .catch((err) => {
        reject(err);
      });
  });
};

// Google Signup
export const googleSignup = (data: { id_token: string; role?: string }) => {
  return new Promise((resolve, reject) => {
    return fetch(`${process.env["REACT_APP_API_BASE_URL"]}/auth/google`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Get Renewal Quote
export const getRenewalQuote = (bookingId: string, term: number = 1, termUnit: "years" | "months" = "years") => {
  return new Promise((resolve, reject) => {
    let _token: string | null = fetchCookie("1Q_SPA");
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/rent/bookings/${bookingId}/renewal-quote?${termUnit}=${term}`,
      {
        headers: {
          Authorization: `Bearer ${_token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

// Renew Booking
export const renewBooking = (payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/extend`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Renew Booking
export const rescheduleBooking = (payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${payload.id}/reschedule`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload.newDate),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Payment
export const makePayment = (id: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/pay-paystack`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

//Verify Payment
export const verifyPayment = (id: string, reference: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/verify-paystack/${reference}`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Upload Legal Document
export const uploadLegalDoc = (id: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/legal/${id}/uploadLegalDocument`,
      {
        // signal: signal,
        headers: {},
        method: "POST",
        body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Create Property
export const createProperty = (payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/properties`, {
      // signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors || data.error || data.success === false) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Add Property Images
export const addProperImages = (
  id: string,

  payload: any,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}/add-images`,
      {
        headers: {},
        method: "PUT",
        body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Delete A property
export const deleteProperty = (id: string): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "DELETE",
        // body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Delete A property
export const toggleVisibility = (id: string): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}/toggle-visibility`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
        // body: payload,
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Update A property
export const updateProperty = (
  id: string | undefined,
  payload: any,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then(
        (data: BaseApiResponse<{}> & { error?: string; errors?: any[] }) => {
          if (data.errors || data.error || data.success === false) {
            reject(data);
          } else if (data.success) {
            resolve(data);
          } else {
            reject(data);
          }
        },
      )
      .catch((err) => {
        reject(err);
      });
  });
};

export const getInsuranceCompanies = () => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/insurance-companies`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// create iBuy property
export const createIBuyProperty = (payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/ibuy-listing`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors || data.error || data.success === false) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const sendOTP = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/auth/send-otp/`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const validateOTP = (
  payload: { otp: string },
  signal: AbortSignal,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/auth/verify-phone/`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getListings = (
  id: string,
  options: { [x: string]: string },
  signal: AbortSignal,
) => {
  let query = Object.keys(options)
    .map((key) => key + "=" + options[key])
    .join("&");
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/${id}/properties?${query}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const login = async (email: string, password: string) => {
  try {
    const response = await axios({
      method: "post",
      url: `${url}/auth/login`,
      data: { email, password },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessage =
        error.response.data?.errors?.[0]?.message ||
        "Login failed. Please try again.";
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error(
        "Unable to connect to the server. Please check your internet connection and try again.",
      );
    } else {
      throw new Error("An unexpected error occurred. Please try again later.");
    }
  }
};
export const logout = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/auth/logout`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const requestSignupOTP = async (email: string) => {
  try {
    const res = await axios.post(`${url}/auth/request-email-verification`, {
      email,
    });
    return res.data;
  } catch (err: any) {
    const message =
      err.response?.data?.errors?.[0]?.message ||
      err.response?.data?.message ||
      "Something went wrong";

    // Throw an Error object so all callers can reliably read err.message.
    throw new Error(message);
  }
};
export const verifySignupOTP = async (email: string, code: string) => {
  try {
    const res = await axios.post(`${url}/auth/verify-email-code`, {
      email,
      code,
    });

    return {
      success: true,
      ...res.data,
    };
  } catch (err: any) {
    const errorMsg =
      err?.response?.data?.errors?.[0]?.message ||
      err?.response?.data?.message ||
      "Something went wrong";

    return {
      success: false,
      message: errorMsg,
    };
  }
};
export const requestResetPasswordOTP = async (email: string) => {
  try {
    const res = await axios.post(`${url}/auth/mobile-forgot-password`, {
      email,
    });
    return res.data;
  } catch (err: any) {
    throw (
      err.response?.data?.errors?.[0]?.message ||
      err.response?.data?.message ||
      "Something went wrong"
    );
  }
};
export const verifyRestPasswordOTP = async (email: string, code: string) => {
  try {
    const res = await axios.post(`${url}/auth/mobile-verify-reset-code`, {
      email,
      code,
    });

    return {
      success: true,
      ...res.data,
    };
  } catch (err: any) {
    const errorMsg =
      err?.response?.data?.errors?.[0]?.message ||
      err?.response?.data?.message ||
      "Something went wrong";

    return {
      success: false,
      message: errorMsg,
    };
  }
};

export const register = async (data: {}) => {
  try {
    const res = await axios.post(`${url}/auth/register`, data);
    return res.data;
  } catch (err: any) {
    toast.error(
      err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.message ||
        "Something went wrong",
    );
    return err?.response?.data || err;
  }
};

// request form end point
export const requestForm = async (data: {}) => {
  return await axios
    .post(`${url}/requests`, data)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      throw err.response.data.errors[0].message;
    });
};

export interface FeaturedPropertyRequestPayload {
  fullName: string;
  propertyLocation: string;
  propertyType: string;
  packageInterest: string;
  phone: string;
  email: string;
}

export const submitFeaturedPropertyRequest = async (
  data: FeaturedPropertyRequestPayload,
) => {
  return await axios
    .post(`${url}/featured-property-requests`, data)
    .then((res) => res.data)
    .catch((err) => {
      throw (
        err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.message ||
        "Something went wrong"
      );
    });
};

export const verifyEmail = async (id: any) => {
  return await axios
    .get(`${url}/auth/verify-email/${id}`)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (err?.response?.data?.message) throw err?.response?.data?.message;
      throw err?.response?.data?.errors[0].message;
    });
};
export const verifyNiesv = async (niesvNumber: any) => {
  return await axios
    .post(`${url}/auth/verify-niesv-number`, { niesvNumber: niesvNumber })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (err?.response?.data?.message) throw err?.response?.data?.message;
      throw err?.response?.data?.errors[0].message;
    });
};
export const verifyAean = async (aeanNumber: any) => {
  return await axios
    .post(`${url}/auth/verify-aean-number`, { aeanNumber: aeanNumber })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (err?.response?.data?.message) throw err?.response?.data?.message;
      throw err?.response?.data?.errors[0].message;
    });
};

export const reActivateAccount = async (id: any) => {
  return await axios
    .get(`${url}/auth/resend-verification-email/${id}`)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (err?.response?.data?.message) throw err?.response?.data?.message;
      throw err?.response?.data?.errors[0].message;
    });
};

export const changePasswordReq = (
  id: string,
  signal: AbortSignal,
  payload: ChangePasswordRequest,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/change-password/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Create Pin
export const createPin = (
  signal: AbortSignal,
  payload: CreatePinRequest,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/create-pin`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Change Pin
export const changePin = (
  id: string,
  signal: AbortSignal,
  payload: ChangePinRequest,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/change-pin/${id}`,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// get categories
export const getCategories = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    return customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/categories`, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const tempDelete = (
  user: string,
  // signal: AbortSignal
  payload: any,
): Promise<BaseApiResponse<NotificationsInterfaceResponse[]>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/kinds/${user}`,
      {
        // signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<NotificationsInterfaceResponse[]>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// KYC
export const getPersonalKycStatus = async () => {
  try {
    const resp = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/kyc?type=PERSONAL`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const data = await resp.json();
    return data?.data;
  } catch (error: any) {
    // toast.error(error?.response?.data?.errors[0]?.message);
  }
};
export const getAgentKycStatus = async () => {
  try {
    const agentResp = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/kyc?type=AGENT_LICENCE`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const data = await agentResp.json();
    return data?.data;
  } catch (error: any) {
    // toast.error(error?.response?.data?.errors[0]?.message);
  }
};

// Search Property by key word
export const searchPropByKey = async (keyword: string, page: number = 1) => {
  try {
    const agentResp = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/listing/search?key=${keyword}&limit=20&page=${page}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const data = await agentResp.json();
    return data;
  } catch (error: any) {
    toast.error(error?.response?.data?.errors[0]?.message);
  }
};

export type SearchSuggestItem = {
  type: "location" | "listing" | "reference";
  label: string;
  query: string;
  subtitle?: string;
};

export const suggestSearchKeys = async (
  keyword: string,
  signal?: AbortSignal,
): Promise<SearchSuggestItem[]> => {
  const key = keyword.trim();
  if (key.length < 2) return [];

  try {
    const agentResp = await customFetch(
      `${
        process.env["REACT_APP_API_BASE_URL"]
      }/listing/suggest?key=${encodeURIComponent(key)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal,
        skipAuthRedirect: true,
      },
    );
    const payload = await agentResp.json();
    return Array.isArray(payload?.suggestions) ? payload.suggestions : [];
  } catch {
    return [];
  }
};
export const searchPropertiesByKeyword = async (
  keyword?: string,
  startDate?: string,
  endDate?: string,
  page: number = 1,
  params?: { [key: string]: string },
  limit: number = 12,
) => {
  try {
    const queryParams = new URLSearchParams();

    if (keyword) queryParams.append("key", keyword);
    if (startDate) queryParams.append("startDate", startDate);
    if (endDate) queryParams.append("endDate", endDate);
    queryParams.append("limit", limit.toString());
    queryParams.append("page", page.toString());

    // Append extra params (like tour=true)
    if (params) {
      Object.keys(params).forEach((key) => {
        queryParams.append(key, params[key]);
      });
    }

    const agentResp = await customFetch(
      `${
        process.env["REACT_APP_API_BASE_URL"]
      }/properties/available?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await agentResp.json();
    return data;
  } catch (error: any) {
    toast.error(
      error?.response?.data?.errors?.[0]?.message ||
        "An error occurred while fetching properties.",
    );
  }
};

// Bank Transfer
export const bankTransferPayment = async () => {
  try {
    const res = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/account-details/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const data = await res.json();
    return data;
  } catch (error: any) {
    toast.error(error?.response?.data?.errors[0]?.message);
  }
};

// Upload Payment proof
export const uploadPaymentProof = async (
  id: string,
  data: FormData,
): Promise<any> => {
  try {
    const res = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/payment-proof`,
      {
        method: "PUT",
        body: data,
      },
    );
    const responseData = await res.json();
    return responseData;
  } catch (error: any) {
    throw error;
  }
};

// Initiate bank transfer booking
export const initiateBankTransfer = (payload: {
  listing: string;
  arrival_date: string;
  departure_date: string;
  guest: {
    adult: number;
    children?: number;
    infants?: number;
    pets: number;
  };
  whatsappNumber: string;
  tc: boolean;
  firstname: string;
  lastname: string;
  phone: string;
  specialRequests: string;
  usePartyPrice?: boolean;
}) => {
  return new Promise((resolve, reject) => {
    let _token: string | null = fetchCookie("1Q_SPA");

    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/initiate-bank-transfer`,
      {
        headers: {
          Authorization: `Bearer ${_token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

// Initiate bank transfer for an existing transaction (per-transaction)
export const initiateTransactionBankTransfer = async (
  transactionId: string,
): Promise<any> => {
  const token = fetchCookie("1Q_SPA");
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/initiate-bank-transfer`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({}),
      },
    )
      .then((res) => res.json())
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => reject(err));
  });
};

// Upload bank transfer proof
export const uploadBankTransferProof = async (
  id: string,
  data: FormData,
): Promise<any> => {
  try {
    const res = await customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/upload-bank-transfer-proof`,
      {
        method: "POST",
        body: data,
      },
    );
    const responseData = await res.json();
    return responseData;
  } catch (error: any) {
    throw error;
  }
};

export const sendAmenitiesChecklist = (transactionId: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/amenities-checklist`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({
          amenitiesChecklist: payload,
        }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const setDamagesCover = (transactionId: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/cover-damages`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const addReferences = (transactionId: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/references`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// export const addReferences = (transactionId: string, payload: any) => {
//
//   return new Promise((resolve, reject) => {
//     return customFetch(
//       `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/references`,
//       {
//         headers: {
//
//           "Content-Type": "application/json",
//         },
//         method: "PUT",
//         body: JSON.stringify(payload),
//       }
//     )
//       .then((res) => {
//         return res.json();
//       })
//       .then((data) => {
//         if (data.errors) {
//           reject(data);
//         } else {
//           resolve(data);
//         }
//       })
//       .catch((err) => {
//         reject(err);
//       });
//   });
// };
export const addStaycation = (payload: any, signal?: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/staycations`, {
      signal: signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getLegalSigningLink = (
  transactionId: string,
  signal: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/legal-signing-link `,
      {
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const addLegalDoc = (id: string, payload: any) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${id}/landlord-legal-details`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const updateAgentAvailabiltyProp = (
  propertyId: string,
  payload: any,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${propertyId}/availability-calendar`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const checkPropertyAvailability = (
  propertyId: string,
  startDate: string,
  endDate: string,
  signal?: AbortSignal,
) => {
  return new Promise<{ success: boolean; isAvailable: boolean }>(
    (resolve, reject) => {
      return customFetch(
        `${process.env["REACT_APP_API_BASE_URL"]}/properties/${propertyId}/is-booked?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
          signal,
        },
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.errors) {
            reject(data);
          } else {
            resolve(data);
          }
        })
        .catch((err) => {
          reject(err);
        });
    },
  );
};

export const getPropAvailabiltyCal = (
  propertyId: string,
  startDate: string,
  endDate: string,
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${propertyId}/availability-calendar?startDate=${startDate}&endDate=${endDate}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const putPropOnHold = (transactionId: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/hold`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const resumeTransaction = (transactionId: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/resume`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const getPropertyPriceSuggestions = (
  address: string,
): Promise<PriceSuggestionResponse> => {
  const encodedAddress = encodeURIComponent(address);

  return new Promise((resolve, reject) => {
    fetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/property-price-suggestions?address=${encodedAddress}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const getKycInfo = (userId: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/kyc-info/${userId}`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const requestKycAccess = (
  { data }: any,
  signal: AbortSignal,
): Promise<KycAccessResponse> => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/kyc-access-requests`,
      {
        method: "POST",
        signal: signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )
      .then((response) => response.json())
      .then((res: KycAccessResponse) => {
        resolve(res);
      })
      .catch((err) => {
        console.error("Error requesting KYC access:", err);
        reject(err);
      });
  });
};

export const deleteUser = (
  id: string,
  reason: string,
  password: string,
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/users/${id}`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "DELETE",
      body: JSON.stringify({ reason, password }),
    })
      .then((res: Response) => res.json())
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err: Error) => reject(err));
  });
};
export const getStates = (signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(`${process.env["REACT_APP_API_BASE_URL"]}/locations/states`, {
      signal,
      headers: {
        "Content-Type": "application/json",
      },
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const getLgasByState = (stateName: string, signal: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/locations/states/${stateName}/lgas`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const applyAsKrentAgent = (
  payload: {
    agentTypes: string[];
    address: string;
    state: string;
    // city: string;
  },
  signal?: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/agent-applications/apply`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const getMyAgentApplication = (signal?: AbortSignal) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/agent-applications/my-application`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const AddPropertyReview = (
  id: string,
  body: { review: string; rating: number },
) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/properties/${id}/reviews`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(body),
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const resumePayment = (transactionId: string) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/resume-payment`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const getCautionFeesSummary = () => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/caution-fees/summary`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};
export const getAllPropertyReviews = (
  propertyId: string,
  page?: number,
  limit?: number,
  signal?: AbortSignal,
) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());

  return new Promise((resolve, reject) => {
    customFetch(
      `${
        process.env["REACT_APP_API_BASE_URL"]
      }/properties/${propertyId}/reviews?${params.toString()}`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const requestTemporaryPin = (
  body: { email: string; password: string },
  signal?: AbortSignal,
) => {
  return new Promise((resolve, reject) => {
    customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/users/request-temporary-pin`,
      {
        signal,
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(body),
      },
    )
      .then((res) => res.json())
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const initiateBooking = (payload: {
  listing: string;
  arrival_date: string;
  departure_date: string;
  usePartyPrice: boolean;
  guest: {
    adult: number;
    children?: number;
    infants?: number;
    pets: number;
  };
}) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/initiate`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const getTransactionByReference = (reference: string) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/reference/${reference}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const downloadReceiptPDF = async (reference: string) => {
  const token = fetchCookie("1Q_SPA");
  const response = await fetch(
    `${process.env["REACT_APP_API_BASE_URL"]}/transactions/receipt/${reference}/pdf`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    let errorMessage = "Failed to download receipt";
    if (errorData.errors && errorData.errors.length > 0) {
      errorMessage = errorData.errors[0].message;
    }
    throw new Error(errorMessage);
  }

  return response.blob();
};

export const respondToHostRequest = (
  transactionId: string,
  action: "accept" | "decline",
) => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/host-respond`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({ action }),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const saveGuestDetails = (
  transactionId: string,
  guestDetails: {
    firstname: string;
    lastname: string;
    phone: string;
    whatsappNumber: string;
    specialRequests: string;
    tc: boolean;
  },
) => {
  return new Promise((resolve, reject) => {
    let _token: string | null = fetchCookie("1Q_SPA");
    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${transactionId}/guest-details`,
      {
        headers: {
          Authorization: `Bearer ${_token}`,
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(guestDetails),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const initiatePayment = (payload: {
  listing: string;
  arrival_date: string;
  departure_date: string;
  guest: {
    adult: number;
    children?: number;
    infants?: number;
    pets: number;
  };
  whatsappNumber: string;
  tc: boolean;
  firstname: string;
  lastname: string;
  phone: string;
  specialRequests: string;
}) => {
  return new Promise((resolve, reject) => {
    let _token: string | null = fetchCookie("1Q_SPA");

    return customFetch(
      `${process.env["REACT_APP_API_BASE_URL"]}/transactions/initiate-payment`,
      {
        headers: {
          Authorization: `Bearer ${_token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err: any) => {
        reject(err);
      });
  });
};

export const requestCautionFeeRefund = async (
  transactionId: string,
  pin: string,
) => {
  const response = await customFetch(
    `${process.env["REACT_APP_API_BASE_URL"]}/transactions/${encodeURIComponent(transactionId)}/caution-fees/request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin }),
    },
  );
  const body = await response.json();
  if (!response.ok || body.success === false) {
    const message =
      body?.errors?.[0]?.message ||
      body?.message ||
      "Unable to submit your refund request. Please try again.";
    throw new Error(message);
  }
  return body;
};

export const applyAsAgent = async (payload: {
  name: string;
  email: string;
  phone: string;
  agencyName: string;
  whatsapp: string;
  location?: string;
  idType: string;
  bankDetails: { accountNumber: string; accountName: string; bankName: string };
  photo: File;
  idDocument: File;
  note?: string;
}) => {
  const form = new FormData();
  form.append("name", payload.name);
  form.append("email", payload.email);
  form.append("phone", payload.phone);
  form.append("agencyName", payload.agencyName);
  form.append("whatsapp", payload.whatsapp);
  if (payload.location) form.append("location", payload.location);
  form.append("idType", payload.idType);
  form.append("bankDetails", JSON.stringify(payload.bankDetails));
  form.append("photo", payload.photo);
  form.append("idDocument", payload.idDocument);
  if (payload.note) form.append("notes", payload.note);

  const response = await customFetch(
    `${process.env["REACT_APP_API_BASE_URL"]}/rent/agents/apply`,
    { method: "POST", body: form },
  );
  const body = await response.json();
  if (!response.ok || body.success === false) {
    const message =
      body?.errors?.[0]?.message ||
      body?.message ||
      "Failed to submit application. Please try again.";
    throw new Error(message);
  }
  return body;
};

export const createRentRenewal = async (bookingId: string, payload: { durationYears?: number; durationMonths?: number }) => {
  const response = await customFetch(
    `${process.env["REACT_APP_API_BASE_URL"]}/rent/bookings/${encodeURIComponent(bookingId)}/renew`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
  const body = await response.json();
  if (!response.ok || body.success === false || body.errors) {
    throw new Error(body.errors?.[0]?.message || body.message || "Could not create renewal booking.");
  }
  return body;
};
